import { Component, Element, Prop, State, h, Build } from '@stencil/core';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

const COMPONENT_NAME = 'sn-reviews';

/**
 * Customer reviews carousel showcasing testimonial quotes with author attribution.
 * Two-column layout: illustration image on the left (5/12) and a swipeable quote carousel on the right (7/12) with prev/next arrows when multiple items exist.
 * Interactive single-quote carousel with avatars and highlight emphasis, distinct from static testimonial grids.
 *
 * @category SignNow
 * @example
 *   <sn-reviews
 *     image="https://marketing-static.signnow.com/366/images/comparison/inner/illustrations-reviews.png"
 *     items='[{"avatar":"https://marketing-static.signnow.com/366/images/comparison/icons/persone-icon-1.png","name":"Sarah Johnson","role":"CEO at TechCorp","quote":"signNow transformed how we handle contracts. Adoption was immediate.","highlight":"Highly recommended!"},{"avatar":"https://marketing-static.signnow.com/366/images/comparison/icons/persone-icon-1.png","name":"Michael Chen","role":"Operations Manager","quote":"The ease of use and pricing is unbeatable compared to DocuSign.","highlight":"Worth every penny."}]'
 *   ></sn-reviews>
 */
@Component({
  tag: 'sn-reviews',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SnReviews {
  @Element() el: HTMLElement;

  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * URL of the illustration image shown on the left side.
   */
  @Prop() image?: string;

  /**
   * Alt text for the illustration image.
   * @default "Customer reviews illustration"
   */
  @Prop() imageAlt?: string;

  /**
   * Array of review items displayed in the carousel.
   * @content
   * @type {array}
   * @item.type {object}
   * @item.property {image} avatar - URL of the reviewer avatar image
   * @item.property {string} name - Reviewer full name
   * @item.property {string} role - Reviewer job title and company
   * @item.property {string} quote - Main quote text
   * @item.property {string} highlight - Bold highlighted text appended to the quote
   * @item.required name
   * @item.required quote
   * @item.content name role quote highlight
   * @minItems 3
   */
  @Prop({ mutable: true }) items?: Array<{
    avatar?: string;
    name: string;
    role?: string;
    quote: string;
    highlight?: string;
  }> | string;

  /**
   * Legacy composite payload.
   * @dynamic
   * @type {object}
   * @property {string} image - Illustration URL (use image prop instead)
   * @property {string} imageAlt - Illustration alt text
   * @property {array} items - Review items (use items prop instead)
   */
  @Prop({ mutable: true }) values?: {
    image?: string;
    imageAlt?: string;
    items?: Array<{ avatar?: string; name: string; role?: string; quote: string; highlight?: string }>;
  } | string;

  /**
   * ARIA label for the previous slide button.
   * @default "Previous review"
   */
  @Prop() ariaLabelPrevious: string = 'Previous review';

  /**
   * ARIA label for the next slide button.
   * @default "Next review"
   */
  @Prop() ariaLabelNext: string = 'Next review';

  @State() currentSlide: number = 0;
  @State() isTransitioning: boolean = false;

  private touchStartX: number = 0;
  private touchEndX: number = 0;

  componentWillLoad() {
    this.values = parseJsonProp(this.values);
    this.items = parseJsonProp(this.items as any);
    if (this.values && typeof this.values !== 'string') {
      if (this.values.items && (!this.items || !(this.items as any[]).length)) {
        this.items = this.values.items;
      }
    }
  }

  private getItems() {
    return Array.isArray(this.items) ? this.items : [];
  }

  private getImage(): string | undefined {
    if (this.image) return this.image;
    if (this.values && typeof this.values !== 'string') return this.values.image;
    return undefined;
  }

  private getImageAlt(): string {
    if (this.imageAlt) return this.imageAlt;
    if (this.values && typeof this.values !== 'string' && this.values.imageAlt) return this.values.imageAlt;
    return 'Customer reviews illustration';
  }

  private goToSlide(index: number) {
    const items = this.getItems();
    if (this.isTransitioning || index === this.currentSlide) return;
    if (index < 0 || index >= items.length) return;
    this.isTransitioning = true;
    this.currentSlide = index;
    if (Build.isBrowser) {
      setTimeout(() => { this.isTransitioning = false; }, 300);
    } else {
      this.isTransitioning = false;
    }
  }

  private nextSlide() {
    const items = this.getItems();
    if (items.length <= 1) return;
    this.goToSlide((this.currentSlide + 1) % items.length);
  }

  private prevSlide() {
    const items = this.getItems();
    if (items.length <= 1) return;
    this.goToSlide((this.currentSlide - 1 + items.length) % items.length);
  }

  private handleTouchStart = (e: TouchEvent) => { this.touchStartX = e.touches[0].clientX; };
  private handleTouchMove = (e: TouchEvent) => { this.touchEndX = e.touches[0].clientX; };
  private handleTouchEnd = () => {
    const diff = this.touchStartX - this.touchEndX;
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    if (Math.abs(diff) > 50) { diff > 0 ? this.nextSlide() : this.prevSlide(); }
    this.touchStartX = 0; this.touchEndX = 0;
  };

  render() {
    const items = this.getItems();
    if (items.length === 0) return null;
    const hasMultiple = items.length > 1;
    const imageSrc = this.getImage();
    const imageAlt = this.getImageAlt();

    return (
      <me-section class={COMPONENT_NAME} yGap="2xl" yGapLg="5xl">
        <me-row align="center">
          {imageSrc && (
            <me-column span="12" spanLg="5" class={`${COMPONENT_NAME}__image-col`}>
              <div class={`${COMPONENT_NAME}__image`}>
                <me-picture src={imageSrc} alt={imageAlt} aspectRatio="1:1"></me-picture>
              </div>
            </me-column>
          )}
          <me-column span="12" spanLg={imageSrc ? '7' : '12'}>
            <div
              class={`${COMPONENT_NAME}__carousel`}
              onTouchStart={this.handleTouchStart}
              onTouchMove={this.handleTouchMove}
              onTouchEnd={this.handleTouchEnd}
              role="region"
              aria-label="Customer reviews"
            >
              <div class={`${COMPONENT_NAME}__card`}>
                <div class={`${COMPONENT_NAME}__slides-author`}>
                  {items.map((item, index) => {
                    const isActive = index === this.currentSlide;
                    return (
                      <div class={`${COMPONENT_NAME}__author-slide ${isActive ? `${COMPONENT_NAME}__author-slide--active` : `${COMPONENT_NAME}__author-slide--hidden`}`} aria-hidden={!isActive ? 'true' : undefined}>
                        <div class={`${COMPONENT_NAME}__author`}>
                          {item.avatar && (
                            <div class={`${COMPONENT_NAME}__avatar`}>
                              <img src={item.avatar} alt={item.name} loading="lazy" />
                            </div>
                          )}
                          <div class={`${COMPONENT_NAME}__author-info`}>
                            <span class={`${COMPONENT_NAME}__author-name`}>{item.name}{item.role ? ',' : ''}</span>
                            {item.role && <span class={`${COMPONENT_NAME}__author-role`}> {item.role}</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div class={`${COMPONENT_NAME}__slides`}>
                  {items.map((item, index) => {
                    const isActive = index === this.currentSlide;
                    return (
                      <div class={`${COMPONENT_NAME}__slide ${isActive ? `${COMPONENT_NAME}__slide--active` : `${COMPONENT_NAME}__slide--hidden`}`} aria-hidden={!isActive ? 'true' : undefined}>
                        <blockquote class={`${COMPONENT_NAME}__quote`}>
                          <p>
                            &ldquo;{item.quote}{' '}
                            {item.highlight && <strong>{item.highlight}</strong>}
                            &rdquo;
                          </p>
                        </blockquote>
                      </div>
                    );
                  })}
                </div>
              </div>
              {hasMultiple && (
                <div class={`${COMPONENT_NAME}__arrows`}>
                  <button type="button" class={`${COMPONENT_NAME}__arrow ${COMPONENT_NAME}__arrow--prev`} aria-label={this.ariaLabelPrevious} onClick={() => this.prevSlide()}>
                    <svg width="36" height="36" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="24" cy="24" r="23" stroke="#9DA9B3" stroke-width="1.5"/><path d="M27 17L21 24L27 31" stroke="#38424B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  </button>
                  <button type="button" class={`${COMPONENT_NAME}__arrow ${COMPONENT_NAME}__arrow--next`} aria-label={this.ariaLabelNext} onClick={() => this.nextSlide()}>
                    <svg width="36" height="36" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="24" cy="24" r="23" stroke="#9DA9B3" stroke-width="1.5"/><path d="M21 17L27 24L21 31" stroke="#38424B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  </button>
                </div>
              )}
            </div>
          </me-column>
        </me-row>
      </me-section>
    );
  }
}
